/* eslint-disable react/jsx-key */
import React, { useEffect, useRef } from 'react';

import {
  TriangleDownIcon,
  TriangleUpIcon,
  HamburgerIcon,
} from '@chakra-ui/icons';
import {
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  CircularProgress,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import { isEmpty } from 'ramda';
import { useHistory } from 'react-router-dom';
import { useTable, useSortBy } from 'react-table';

import AppModalView from 'components/modules/AppModal';
import Button from 'components/primitives/Button';
import PaginationView from 'components/primitives/Pagination';
import { Search, Listing as ListingIcon } from 'components/primitives/SVG';
import Typography from 'components/primitives/Typography';
import { routesSettings } from 'constants/routes';
import { useTheme } from 'utils/theme';

import { EmailMarketingGeneratedProps } from './EmailMarketing.props';

const widthPerAccessor = {
  listingType: 150,
  price: 150,
  undefined: undefined,
};

const EmailMarketingView = ({
  data,
  meta,
  isLoading,
  tableOptions,
  setTableOptions,
  onDelete,
  ...props
}: EmailMarketingGeneratedProps): JSX.Element => {
  const history = useHistory();
  const theme = useTheme();

  const searchRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const columns = React.useMemo(
    () => [
      {
        Header: 'Email Subject',
        accessor: 'email_subject',
      },
      {
        Header: 'Email Send to',
        accessor: 'user_type',
      },
      {
        Header: 'Scheduled Date',
        accessor: 'campaign_time',
      },
      {
        Header: 'Created On',
        accessor: 'createdAt',
      },
      {
        Header: 'Action',
        accessor: 'actionRequired',
        Cell: ({ row }: any) => {
          const { isOpen, onOpen, onClose } = useDisclosure();

          return (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ position: 'relative' }}
            >
              {/* Action Menu */}
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<HamburgerIcon color="rgb(28, 0, 102)" />}
                  border="1px solid transparent"
                  backgroundColor="transparent"
                  onClick={(e) => e.stopPropagation()} // Prevent row click event
                />
                <MenuList onClick={(e) => e.stopPropagation()}>
                  <MenuItem onClick={onOpen}>Delete</MenuItem>{' '}
                </MenuList>
              </Menu>

              {/* Confirmation Modal */}
              {isOpen && (
                <AppModalView
                  isOpen={isOpen}
                  onClose={onClose}
                  title="Delete Campaign"
                  FooterComponent={
                    <>
                      <Button
                        onClick={() => onDelete(row.original._id, onClose)}
                      >
                        Delete
                      </Button>
                    </>
                  }
                >
                  Are you sure you want to delete this campaign?
                </AppModalView>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      data,
      // @ts-ignore
      columns,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortBy: isLoading,
    },
    useSortBy
  );

  const onPaginate = (page: number, pageSize: number) => {
    setTableOptions((prevState) => {
      return {
        ...prevState,
        page: prevState.limit !== pageSize ? 1 : page,
        limit: pageSize,
      };
    });
  };

  useEffect(() => {
    setTableOptions((prevState) => {
      if (!isEmpty(sortBy)) {
        const { id, desc } = sortBy[0];
        return {
          ...prevState,
          sort: id,
          direction: desc ? 'desc' : 'asc',
        };
      } else if (isEmpty(sortBy) && !!prevState.sort) {
        const psCopy = { ...prevState };
        delete psCopy.sort;
        delete psCopy.direction;

        return psCopy;
      }

      return prevState;
    });
  }, [sortBy]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="type5" weight="700" color="primary">
          Email Marketing
        </Typography>
        <Box justifyContent="Box-end">
          <Button
            variant="small"
            onClick={() => history.push(routesSettings.CREATE_TEMPLATE.path)}
          >
            Create Email Template
          </Button>
        </Box>
      </Box>
      <Box
        mt={5}
        borderRadius={10}
        p={5}
        minHeight="500px"
        bg="#fff"
        filter="drop-shadow(0px 2px 10px rgba(149, 149, 149, 0.18))"
      >
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="Box-start"
        >
          <Box height="30px" alignItems="center" display="flex">
            <ListingIcon color="shade10" width={12} height={12} />
            <Box mx={3}>
              <Typography variant="type8" weight="600" color="shade10">
                ALL EMAILS
              </Typography>
            </Box>
            {isLoading ? (
              <CircularProgress
                isIndeterminate
                size="20px"
                color={theme.colors.primary}
              />
            ) : (
              <Typography variant="type10" color="shade5">
                {meta.count} E-mails
              </Typography>
            )}
          </Box>
          <HStack spacing={5}>
            <InputGroup size="sm" width={300}>
              <InputLeftAddon borderRadius={10} backgroundColor="transparent">
                <Search width="14px" height="14px" />
              </InputLeftAddon>
              <Input
                ref={searchRef}
                onChange={(a: any) => props.onSearchKey(a.target.value)}
                borderRadius={10}
                borderLeft={'none'}
                placeholder={'Search'}
              />
            </InputGroup>
          </HStack>
        </Box>
        <Table mt={7} {...getTableProps()}>
          <Thead backgroundColor="shade12">
            {headerGroups.map((headerGroup: any) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <Th
                    {
                      // @ts-ignore
                      ...column.getHeaderProps(column.getSortByToggleProps())
                    }
                  >
                    <Box>
                      <Typography variant="type9" weight="600" color="shade5">
                        {column.Header}
                      </Typography>
                      <Box ml={2}>
                        {
                          // @ts-ignore
                          column.isSorted ? (
                            <Box>
                              {
                                // @ts-ignore
                                column.isSortedDesc ? (
                                  <TriangleDownIcon aria-label="sorted descending" />
                                ) : (
                                  <TriangleUpIcon aria-label="sorted ascending" />
                                )
                              }
                            </Box>
                          ) : (
                            <Box width="12px" height="5px" />
                          )
                        }
                      </Box>
                    </Box>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody mt={7} {...getTableBodyProps()}>
            {rows.map((row: any, i) => {
              prepareRow(row);
              return (
                <Tr
                  backgroundColor={i % 2 !== 0 ? 'shade18' : 'transparent'}
                  cursor="pointer"
                  onClick={() =>
                    history.push(
                      `${routesSettings.CREATE_TEMPLATE.path}?id=${row.original._id}`
                    )
                  }
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell: any) => (
                    <Td
                      {...cell.getCellProps()}
                      // @ts-ignore
                      width={widthPerAccessor[cell.column.id]}
                    >
                      <Typography variant="type8" color="shade5">
                        {cell.render('Cell')}
                      </Typography>
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>

        <Box mt={5} width="100%" display="flex" justifyContent="end">
          <PaginationView
            disabled={isLoading}
            total={meta.count}
            current={meta.page}
            pageSize={tableOptions.limit}
            onChange={onPaginate}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EmailMarketingView;
